import { AuditLogBox } from "@/components/common/AuditLogBox";
import { DocumentsTab } from "@/components/common/DocumentsTab";
import { PageHeader } from "@/components/common/PageHeader";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useRecordContext, useGetOne, Tab } from "react-admin";
import { ResourceName, Reimbursement, Claimant, Client } from "@/types";
import { SwapHoriz as ReassignIcon } from "@mui/icons-material";
import { ReassignReimbursementDialog } from "./components/ReassignReimbursementDialog";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { useState } from "react";
import { getResourceIcon } from "@/utils/icons";
import { PageContent } from "@/components/common/PageLayout";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { RecordCommentsButton } from "@/components/common/comments/RecordCommentsButton";
import { ReimbursementSideDrawer } from "./components/ReimbursementSideDrawer";
import { ReimbursementMetrics } from "./components/ReimbursementMetrics";
import { ReimbursementOverviewTab } from "./components/ReimbursementOverviewTab";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ViewTasksButton } from "../tasks/components/ViewTasksButton";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

const ReimbursementPageHeader = ({ rightContent }: { rightContent: React.ReactNode }) => {
  const record = useRecordContext<Reimbursement>();
  const ReimbursementsIcon = getResourceIcon(ResourceName.REIMBURSEMENTS);

  const claimantId = record?.claimant_id_ref;
  const clientId = record?.client_id_ref;

  const { data: claimant } = useGetOne<Claimant>(
    ResourceName.CLAIMANTS,
    { id: claimantId || "" },
    { enabled: !!claimantId }
  );

  const { data: client } = useGetOne<Client>(ResourceName.CLIENTS, { id: clientId || "" }, { enabled: !!clientId });

  const title = record?.requested_amount ? `Reimbursement · ${formatUSD(record.requested_amount)}` : "Reimbursement";
  const subtitle =
    [claimant?.claimant_name, client?.client_legal_name].filter(Boolean).join(" · ") ||
    (claimantId ? "Loading..." : "");

  return (
    <PageHeader
      customTitle={title}
      customSubtitle={subtitle}
      statusKey="status"
      rightContent={rightContent}
      headerIcon={<ReimbursementsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      customTitleTag={record?.aegle_id}
    />
  );
};

const ReimbursementShowInner = () => {
  const record = useRecordContext<Reimbursement>();
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const toggleDrawer = () => setDrawerExpanded((prev) => !prev);

  return (
    <>
      <Box
        sx={{
          transition: "margin-right 0.3s ease",
          marginRight: drawerExpanded ? "500px" : 0,
        }}
      >
        <ReimbursementPageHeader
          rightContent={
            <ReimbursementShowActions
              setDrawerExpanded={setDrawerExpanded}
              drawerExpanded={drawerExpanded}
              onReassign={() => setReassignOpen(true)}
            />
          }
        />
        <PageContent>
          <Box pb={4}>
            <ReimbursementMetrics />
          </Box>
          <ReimbursementTabbedShowLayout />
        </PageContent>
      </Box>
      <ReimbursementSideDrawer drawerExpanded={drawerExpanded} toggleDrawer={toggleDrawer} />
      {record && (
        <ReassignReimbursementDialog open={reassignOpen} onClose={() => setReassignOpen(false)} record={record} />
      )}
    </>
  );
};

export const ReimbursementShow = () => (
  <StyledShow actions={false}>
    <ReimbursementShowInner />
  </StyledShow>
);

const ReimbursementShowActions = ({
  setDrawerExpanded,
  drawerExpanded,
  onReassign,
}: {
  setDrawerExpanded: (b: boolean) => void;
  drawerExpanded: boolean;
  onReassign: () => void;
}) => {
  const { canDelete, canEdit } = useResourcePermissions(ResourceName.REIMBURSEMENTS);
  const { canList: canListTasks } = useResourcePermissions(ResourceName.TASKS);
  const record = useRecordContext<Reimbursement>();

  return (
    <PageHeaderActions
      actions={{
        delete: canDelete,
      }}
    >
      {canEdit && (
        <Tooltip title="Reassign reimbursement">
          <IconButton size="small" onClick={onReassign} sx={{ color: "primary.main" }}>
            <ReassignIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!drawerExpanded && canListTasks && <ViewTasksButton onClick={() => setDrawerExpanded(true)} />}
      <RecordCommentsButton
        subjectType={ResourceName.REIMBURSEMENTS}
        subjectId={record?.id ? String(record.id) : undefined}
      />
    </PageHeaderActions>
  );
};

const ReimbursementTabbedShowLayout = () => {
  const reimbursementRecord = useRecordContext<Reimbursement>();

  return (
    <StyledTabbedShowLayout>
      <Tab label="Overview">
        <ReimbursementOverviewTab />
      </Tab>
      {reimbursementRecord?.source_documents && (
        <Tab label="Documents">
          <DocumentsTab documentIds={reimbursementRecord.source_documents} />
        </Tab>
      )}
      <Tab label="Audit Log">
        <AuditLogBox tableName={ResourceName.REIMBURSEMENTS} />
      </Tab>
    </StyledTabbedShowLayout>
  );
};
